import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronLeft, X, BookOpen, Download, Loader2, Volume2, VolumeX, Pause, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { LessonSectionHeading, LessonTextContent } from './lesson-text/LessonTextContent';
import './lesson-text/LessonDialog.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StoryViewer = ({ open, onClose, story, lessonId, isCompleted, onMarkComplete, markingComplete }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioCache, setAudioCache] = useState({});
  const [diacritizedTexts, setDiacritizedTexts] = useState({});
  const audioRef = useRef(null);
  const { getAuthHeaders } = useAuth();

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  useEffect(() => {
    return () => stopAudio();
  }, []);

  // Fetch cached diacritized texts when story opens
  useEffect(() => {
    if (open && lessonId) {
      axios.get(`${API_URL}/api/stories/${lessonId}/diacritized`)
        .then(res => {
          if (res.data?.diacritized_texts) {
            const texts = {};
            Object.entries(res.data.diacritized_texts).forEach(([k, v]) => {
              texts[parseInt(k)] = v;
            });
            setDiacritizedTexts(texts);
          }
        })
        .catch(() => {});
    }
  }, [open, lessonId]);

  if (!open || !story || !story.sections) return null;

  const totalPages = story.sections.length;
  const section = story.sections[currentPage];

  const goNext = () => {
    if (currentPage < totalPages - 1) {
      stopAudio();
      setCurrentPage(currentPage + 1);
    }
  };

  const goPrev = () => {
    if (currentPage > 0) {
      stopAudio();
      setCurrentPage(currentPage - 1);
    }
  };

  const handleReadAloud = async () => {
    if (audioLoading) return;

    // If already playing, stop
    if (isPlaying) {
      stopAudio();
      return;
    }

    // Check local cache first
    if (audioCache[currentPage]) {
      playAudio(audioCache[currentPage]);
      return;
    }

    setAudioLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/tts-story`,
        {
          lesson_id: lessonId,
          page_index: currentPage,
          text: section.text
        },
        { headers: getAuthHeaders(), timeout: 120000 }
      );

      const base64Audio = response.data.audio_base64;
      setAudioCache(prev => ({ ...prev, [currentPage]: base64Audio }));
      
      // Save diacritized text if returned
      if (response.data.diacritized_text) {
        setDiacritizedTexts(prev => ({ ...prev, [currentPage]: response.data.diacritized_text }));
      }
      
      playAudio(base64Audio);
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      setAudioLoading(false);
    }
  };

  const playAudio = (base64Audio) => {
    stopAudio();
    const audio = new Audio(`data:audio/mp3;base64,${base64Audio}`);
    audioRef.current = audio;
    audio.onended = () => setIsPlaying(false);
    audio.play();
    setIsPlaying(true);
  };

  const handleDownloadPDF = () => {
    setDownloading(true);
    try {
      const printWindow = window.open('', '_blank');
      const sectionsHtml = story.sections.map((s, idx) => `
        <div style="page-break-inside: avoid; margin-bottom: 40px; ${idx > 0 ? 'page-break-before: always;' : ''}">
          <h2 style="text-align: center; color: #b45309; font-size: 24px; margin-bottom: 20px;">
            ${s.section_title}
          </h2>
          ${s.image_base64 ? `
            <div style="text-align: center; margin-bottom: 20px;">
              <img src="data:image/png;base64,${s.image_base64}" 
                   style="max-width: 400px; width: 100%; border-radius: 16px; border: 2px solid #f59e0b;" />
            </div>
          ` : ''}
          <div style="background: #fef3c7; padding: 20px; border-radius: 12px; border: 1px solid #fcd34d;">
            <p style="font-size: 20px; line-height: 2.2; color: #1a1a2e; text-align: right;">
              ${s.text}
            </p>
          </div>
        </div>
      `).join('');

      printWindow.document.write(`
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
          <head>
            <meta charset="UTF-8">
            <title>${story.title}</title>
            <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700&display=swap" rel="stylesheet">
            <style>
              * { box-sizing: border-box; }
              body {
                font-family: 'Tajawal', Arial, sans-serif;
                direction: rtl;
                margin: 0;
                padding: 0;
                background: white;
              }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div style="max-width: 700px; margin: 0 auto; padding: 40px 30px;">
              <div style="text-align: center; margin-bottom: 40px; padding: 30px; background: linear-gradient(135deg, #f59e0b22, #d9770622); border-radius: 16px; border: 2px solid #f59e0b;">
                <h1 style="font-size: 32px; color: #b45309; margin: 0 0 10px 0;">${story.title}</h1>
                <p style="color: #92400e; font-size: 16px; margin: 0;">قصة تعليمية مصورة</p>
              </div>
              ${sectionsHtml}
              <div style="text-align: center; padding: 20px; color: #9ca3af; font-size: 14px; border-top: 1px solid #e5e7eb; margin-top: 30px;">
                RasheedGPT - منصة رشيد جي بي تي التعليمية
              </div>
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() { window.print(); }, 500);
                window.onafterprint = function() { window.close(); }
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    } catch (error) {
      console.error('PDF download error:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stopAudio(); onClose(); setCurrentPage(0); } }}>
      <DialogContent
        className="lesson-reader-dialog lesson-story-dialog max-w-3xl w-[95vw] bg-[#1e1e3a] border-white/10 text-white p-0 max-h-[75vh] !grid-rows-none !gap-0 flex flex-col rounded-xl overflow-hidden"
        data-testid="story-viewer-dialog"
      >
        <DialogDescription className="sr-only">قصة الدرس المصورة مع صفحات القراءة</DialogDescription>
        {/* Story Header */}
        <div className="bg-gradient-to-l from-amber-600/30 to-orange-500/20 px-6 py-4 flex items-center justify-between border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/30 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-white tracking-normal" data-testid="story-title">{story.title}</DialogTitle>
              <p className="text-xs text-white/50">صفحة {currentPage + 1} من {totalPages}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="text-amber-300 hover:text-amber-200 hover:bg-white/10"
              data-testid="story-download-pdf-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin me-1" /> : <Download className="w-4 h-4 me-1" />}
              <span className="text-xs">تحميل PDF</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { onClose(); setCurrentPage(0); }}
              className="text-white/60 hover:text-white hover:bg-white/10 rounded-full"
              data-testid="story-close-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Story Content */}
        <div className="overflow-y-auto flex-1 min-h-0 px-6 py-5" data-testid="story-content-area">
          {/* Section Title */}
          <LessonSectionHeading number={currentPage + 1} tone={['rose', 'sky', 'sun', 'leaf'][currentPage % 4]} level="h3" testId={`story-section-title-${currentPage}`}>{section.section_title}</LessonSectionHeading>

          {/* Illustration */}
          {section.image_base64 ? (
            <div className="mb-5 flex justify-center">
              <div className="rounded-2xl overflow-hidden border-2 border-amber-400/30 shadow-lg shadow-amber-500/10 max-w-md w-full">
                <img
                  src={`data:image/png;base64,${section.image_base64}`}
                  alt={section.section_title}
                  className="w-full h-auto object-cover"
                  data-testid={`story-image-${currentPage}`}
                />
              </div>
            </div>
          ) : (
            <div className="mb-5 flex justify-center">
              <div className="rounded-2xl border-2 border-dashed border-white/20 w-full max-w-md h-48 flex items-center justify-center bg-white/5">
                <p className="text-white/30 text-sm">لم يتم توليد الصورة</p>
              </div>
            </div>
          )}

          {/* Story Text */}
          <div
            dir="rtl"
            className="text-right bg-white/5 rounded-xl p-5 border border-white/10 relative"
            data-testid={`story-text-${currentPage}`}
          >
            <button
              onClick={handleReadAloud}
              disabled={audioLoading}
              className={`absolute top-3 left-3 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                isPlaying 
                  ? 'bg-amber-500 text-white animate-pulse' 
                  : audioLoading
                    ? 'bg-white/10 text-white/40'
                    : 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/40'
              }`}
              data-testid="story-read-aloud-btn"
              title={isPlaying ? 'إيقاف' : 'اقرأ لي'}
            >
              {audioLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </button>
            <div className="story-reading-body"><LessonTextContent compact content={diacritizedTexts[currentPage] || section.text} testId={`story-reading-text-${currentPage}`} /></div>
          </div>
        </div>

        {/* Mark as Complete - shows on last page */}
        {currentPage === totalPages - 1 && (
          <div className="px-6 pb-2 shrink-0">
            {isCompleted ? (
              <div className="flex items-center justify-center gap-2 p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30" data-testid="story-mark-complete-done">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-emerald-400 font-semibold">تمت القراءة</span>
              </div>
            ) : (
              <Button
                onClick={onMarkComplete}
                disabled={markingComplete}
                className="w-full bg-white/10 hover:bg-emerald-500/20 hover:border-emerald-500/40 border border-white/20 text-white/80 hover:text-emerald-300 transition-all"
                data-testid="story-mark-complete-btn"
              >
                {markingComplete ? (
                  <span className="animate-pulse">جارٍ الحفظ...</span>
                ) : (
                  <span className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    تم قراءتها
                  </span>
                )}
              </Button>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between bg-[#1a1a2e] shrink-0 rounded-b-xl">
          <Button
            variant="ghost"
            onClick={goPrev}
            disabled={currentPage === 0}
            className="text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30"
            data-testid="story-prev-btn"
          >
            <ChevronRight className="w-5 h-5 me-1" />
            السابق
          </Button>

          {/* Page dots */}
          <div className="flex gap-2">
            {story.sections.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentPage(idx)}
                aria-label={`الصفحة ${idx + 1}`}
                aria-current={idx === currentPage ? 'page' : undefined}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentPage
                    ? 'bg-amber-400 scale-125'
                    : 'bg-white/20 hover:bg-white/40'
                }`}
                data-testid={`story-page-dot-${idx}`}
              />
            ))}
          </div>

          <Button
            variant="ghost"
            onClick={goNext}
            disabled={currentPage === totalPages - 1}
            className="text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-30"
            data-testid="story-next-btn"
          >
            التالي
            <ChevronLeft className="w-5 h-5 ms-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StoryViewer;
